import { FC, useEffect, useRef } from "react";
import { FormikErrors, FormikProps } from "formik";
import { WorkCreationSecondStep } from "../../../domain/models/dto/WorkDTO";
import CustomInput from "../../../custom-components/form-inputs/CustomInput";
import { workCreationSecondStepFields } from "../../../validation-schemas/work-schemas/WorkCreationSchema";
import PhoneInfo from "../../resources/components/phone-info/PhoneInfo";
import { useTranslation } from "react-i18next";
import CreateWorkRadioButtonSection from "./CreateWorkRadioButtonSection";
import CustomError from "../../../custom-components/form-inputs/CustomError";
import useScreenSize from "../../../custom-hooks/base-hooks/useScreenSize";
import InvalidSVG from "../../resources/svg/functional/InvalidSVG";
import ValidSVG from "../../resources/svg/functional/ValidSVG";
import { PhoneInfoForm } from "../../../domain/models/dto/PhoneDTO";
import CreateWorkValidationIcons from "./CreateWorkValidationIcons";
import CalendarIconSVG from "../../resources/svg/CalendarIconSVG";
import {
    getCurrentDatetimeWithCorrectionMin,
    getCurrentDatetimeWithCorrectionYears,
} from "../../../tools/DatetimeTools";
import { workStartFromOffsetMin } from "../../../Config";
import { MILLISECONDS_IN_MINUTE } from "../../../domain/constants";

interface CreateWorkSecondStepProps {
    form: FormikProps<WorkCreationSecondStep>;
    errorKey: string;
}

const CreateWorkSecondStep: FC<CreateWorkSecondStepProps> = (props) => {
    const { t } = useTranslation();
    const { upTo650, moreOrEqual650, upTo1024, moreOrEqual1024 } = useScreenSize();
    const dateInputRef = useRef<HTMLInputElement>(null);

    const validContacts =
        (props.form.values.phones.some((phone) => phone?.phone) || props.form.values.telegram) &&
        (!props.form.errors.phones ||
            (props.form.errors.phones as FormikErrors<PhoneInfoForm[]>).every((phone) => !phone?.phone)) &&
        !props.form.errors.telegram;

    const showDatePickerHandler = () => {
        if (dateInputRef.current) {
            dateInputRef.current.showPicker();
        }
    };

    useEffect(() => {
        workCreationSecondStepFields.startFrom.initial = getCurrentDatetimeWithCorrectionMin(-workStartFromOffsetMin);
        const interval = setInterval(() => {
            const newMinValue = getCurrentDatetimeWithCorrectionMin(-workStartFromOffsetMin);
            workCreationSecondStepFields.startFrom.min = {
                value: newMinValue,
                key: "workCreation.fields.min.startFrom",
            };
            workCreationSecondStepFields.startFrom.max = {
                value: getCurrentDatetimeWithCorrectionYears(1),
                key: "workCreation.fields.max.startFrom",
            };

            props.form.setFieldValue("startFrom", newMinValue);
        }, workStartFromOffsetMin * MILLISECONDS_IN_MINUTE);

        return () => clearInterval(interval);
    }, []);

    return (
        <div className="relative w-full">
            <div className="relative flex tmax4:flex-col tmin4:items-center w-full mb-6">
                <div className="relative flex flex-col">
                    <div className="flex flex-col">
                        {upTo650 && (
                            <div className="flex items-center tmax4:mb-3">
                                <p className="H20B dmax2:H16B text-textWhite mr-5">
                                    {t("workCreation.fields.workStart")}
                                </p>
                                <CreateWorkValidationIcons names={["startFrom"]} form={props.form} />
                            </div>
                        )}
                        <div className="relative flex items-center">
                            <div className="relative flex items-center">
                                {moreOrEqual650 && (
                                    <p className="H20B dmax2:H16B text-textWhite mr-5 tmax4:mb-3">
                                        {t("workCreation.fields.workStart")}
                                    </p>
                                )}
                                <CustomInput
                                    form={props.form}
                                    name="startFrom"
                                    fields={workCreationSecondStepFields}
                                    inputRef={dateInputRef}
                                    className="h-[60px] dmax2:h-[45px] min-w-[280px]"
                                />
                                <CalendarIconSVG
                                    onClick={showDatePickerHandler}
                                    className="absolute dmax2:w-6 dmax2:h-6 right-[20px] dmax2:right-[25px] top-3 dmax2:top-[10px] text-textFaded cursor-pointer"
                                    fill="#4B4B52"
                                />
                            </div>
                            {moreOrEqual650 && (
                                <CreateWorkValidationIcons
                                    names={["startFrom"]}
                                    form={props.form}
                                    wrapClassName="ml-3"
                                />
                            )}
                        </div>
                    </div>
                    <CustomError name="startFrom" form={props.form} className="mt-2 dmax2:mt-1" />
                </div>
            </div>
            <CreateWorkRadioButtonSection form={props.form} />
            <div className="mt-9 dmax2:mt-6">
                <div className="flex items-center mb-5 dmax2:mb-4">
                    <p className="H24B dmax2:H16B text-textWhite pb-[2px]">{t("common.contacts")}</p>
                    {(props.form.touched.phones || props.form.touched.telegram) && (
                        <div className="ml-3">
                            {validContacts ? (
                                <ValidSVG className="min-w-8 dmax2:min-w-6 h-8 dmax2:h-6" />
                            ) : (
                                <InvalidSVG className="min-w-8 dmax2:min-w-6 h-8 dmax2:h-6" />
                            )}
                        </div>
                    )}
                </div>
                <p className="H16 tmax4:H14 text-textFaded w-[650px] ti6:w-[507px] ti7:w-[420px] tmax4:w-[320px]">
                    {t("userProfile.editProfile.contactsRule")}
                </p>
                <div className="flex justify-between">
                    <div className="flex flex-col w-full di6:w-[674px] td5:w-[506px] ti7:w-[444px] tmax4:w-[318px]">
                        <PhoneInfo form={props.form} fields={workCreationSecondStepFields} />
                        <div className="flex items-center mt-4 dmax2:mt-3 pb-[1px]">
                            <div className="flex flex-col w-full">
                                <div className="flex items-center tmax3:justify-between">
                                    <div className="flex items-center H16B dmax2:H14B text-textWhite tmax4:w-[75px] td4:min-w-[135px] dmin2:w-[200px] tmin3:mr-3">
                                        <img
                                            src="/images/telegram.png"
                                            alt="telegram"
                                            className="w-9 tmax4:w-8 h-9 tmax4:h-8 mr-3 dmax2:mr-2"
                                        />
                                        {t("userProfile.fields.telegramField")}
                                    </div>
                                    <CustomInput
                                        form={props.form}
                                        name="telegram"
                                        fields={workCreationSecondStepFields}
                                        className="w-[330px] di6:w-[300px] ti7:w-[250px] tmax4:w-[204px] h-[52px] dmax2:h-[45px]"
                                        onChange={(e) => {
                                            const raw = e?.target.value || "";
                                            const sanitized = raw
                                                .replace(/^(https?:\/\/)?(t\.me\/|telegram\.me\/)/i, "")
                                                .replace(/[^a-zA-Z0-9_@]/g, "")
                                                .slice(0, 33);
                                            props.form.setFieldValue("telegram", sanitized);
                                        }}
                                    />
                                </div>
                                {moreOrEqual1024 && (
                                    <CustomError
                                        errorKey={props.form.errors.telegram}
                                        className="dmin2:pl-[212px] di6:pl-[148px] mt-2 dmax2:mt-1"
                                    />
                                )}
                            </div>
                        </div>
                        {upTo1024 && (
                            <CustomError errorKey={props.form.errors.telegram} className="mt-2 dmax2:mt-1 text-end" />
                        )}
                    </div>
                </div>
            </div>
            <CustomError errorKey={props.errorKey} className="mt-4 dmax2:mt-3" />
            <button
                className="button H20 dmax2:H14B w-[193px] dmax2:w-[175px] h-[50px] dmax2:h-[40px] mt-9 dmax2:mt-6"
                onClick={() => props.form.handleSubmit()}
            >
                <div className="mb-[2px]">{t("workCreation.createWorkAction")}</div>
            </button>
        </div>
    );
};

export default CreateWorkSecondStep;
